/**
 * The words a door's position answers to (D216) — `open` and `opened` while
 * it stands open, `closed` and `shut` while it does not.
 *
 * @remarks
 * Mike's ruling, 2026-09-28: in `examine open jar` the word `open` describes
 * the jar, and WHICH jar it describes is the world's to say, not a word an
 * author bolts onto the catalog. So a thing with a door answers these words
 * from its live state, the way lockers eleven and twelve should always have
 * been told apart: `examine open locker` finds eleven once the bar has forced
 * it, and finds nothing while both are shut.
 *
 * The state is the ONLY vote. A door's words are taken out of its page words
 * and its declared adjectives, because both are static and the door is not:
 * locker eleven's own refusal says "Locker eleven is open, and it is going to
 * stay that way", and D205 folded that line once at load, so before the bar
 * ever touched it `examine open locker` could reach a shut locker through a
 * sentence the player had not yet been told. A page may say what a thing
 * looks like; whether its door is open is a fact the engine keeps.
 *
 * Only `openable` things carry these. A thing with no door may still be
 * described as open by its own prose — the burying ground's graves are "open
 * to the sky", and no verb will ever shut them — and there the page and the
 * adjectives keep their say.
 *
 * English words in the engine, deliberately, for now: they are the
 * vocabulary's in spirit, and they move there the day a pack is written in
 * a language whose doors open in other words.
 *
 * @packageDocumentation
 */

import type { ItemDefinition, RuntimeState } from '@orpheus/content-schema';

import { isContainerOpen } from './item-state.js';

/** What an open door answers to. */
export const OPEN_DOOR_WORDS: readonly string[] = ['open', 'opened'];

/** What a shut door answers to. */
export const SHUT_DOOR_WORDS: readonly string[] = ['closed', 'shut'];

/** Every word a door's position can answer to, whichever way it stands. */
export const DOOR_STATE_WORDS: ReadonlySet<string> = new Set([...OPEN_DOOR_WORDS, ...SHUT_DOOR_WORDS]);

/**
 * The words this thing's door answers to right now — none when it has no door.
 *
 * @param item - The thing
 * @param state - The live runtime state
 * @returns The door's live words
 */
export const liveDoorWordsOf = (item: ItemDefinition, state: RuntimeState): readonly string[] => {
	if (item.openable === undefined) {
		return [];
	}

	return isContainerOpen(item, state) ? OPEN_DOOR_WORDS : SHUT_DOOR_WORDS;
};

/**
 * Every word this thing's door could ever answer to — the STATIC walks' view
 * (D207), which asks whether a phrase could ever resolve and takes no state.
 *
 * @param item - The thing
 * @returns Both positions' words for a thing with a door; none otherwise
 */
export const everyDoorWordOf = (item: ItemDefinition): readonly string[] =>
	item.openable === undefined ? [] : [...OPEN_DOOR_WORDS, ...SHUT_DOOR_WORDS];

/**
 * Take a door's words out of a static word list, for a thing with a door —
 * its state speaks for it instead (see the module remarks).
 *
 * @param item - The thing
 * @param words - Its page words or its declared adjectives
 * @returns The words its state does not own
 */
export const withoutDoorWords = (item: ItemDefinition, words: readonly string[]): readonly string[] =>
	item.openable === undefined ? words : words.filter((word) => !DOOR_STATE_WORDS.has(word.trim().toLowerCase()));
